import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/ui/Header';
import Sidebar from '../components/ui/Sidebar';
import Breadcrumb from '../components/ui/Breadcrumb';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import axiosInstance from '../utils/axios';
import { toast } from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';

const SecuritySettings = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  // =====================================
  // 2FA STATE MANAGEMENT
  // =====================================
  const [twoFactorStatus, setTwoFactorStatus] = useState({
    enabled: false,
    method: null,
    confirmed_at: null,
    phone_number: '',
    masked_phone: '',
    email: '',
    masked_email: '',
    recovery_codes_count: 0,
    has_recovery_codes: false,
  });
  const [isLoading2Fa, setIsLoading2Fa] = useState(true);

  // Setup Wizard State
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [activeTab, setActiveTab] = useState('authenticator'); // 'authenticator', 'sms', 'email'
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // Authenticator state
  const [authSetupData, setAuthSetupData] = useState({ secret: '', otpauth_url: '' });
  const [isLoadingAuthSetup, setIsLoadingAuthSetup] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  // SMS OTP state
  const [smsPhone, setSmsPhone] = useState('');
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [smsCooldown, setSmsCooldown] = useState(0);
  const [smsSentNotice, setSmsSentNotice] = useState('');
  const [smsDevOtp, setSmsDevOtp] = useState('');
  const [smsEmailBackupNotice, setSmsEmailBackupNotice] = useState(false);
  const [smsCarrierSent, setSmsCarrierSent] = useState(false);

  // Email OTP state
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailCooldown, setEmailCooldown] = useState(0);
  const [emailSentNotice, setEmailSentNotice] = useState('');
  const [emailDevOtp, setEmailDevOtp] = useState('');

  // Recovery Codes Modal
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [isRegeneratingCodes, setIsRegeneratingCodes] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);

  // Disable 2FA Modal
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [isDisabling2Fa, setIsDisabling2Fa] = useState(false);
  const [disableError, setDisableError] = useState('');

  // Password form state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: ''
  });

  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false
  });

  const [passwordErrors, setPasswordErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  // Load 2FA status on mount
  useEffect(() => {
    fetch2FaStatus();
  }, []);

  // Cooldown timers
  useEffect(() => {
    if (smsCooldown <= 0) return;
    const t = setInterval(() => setSmsCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [smsCooldown]);

  useEffect(() => {
    if (emailCooldown <= 0) return;
    const t = setInterval(() => setEmailCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [emailCooldown]);

  const fetch2FaStatus = async () => {
    try {
      setIsLoading2Fa(true);
      const res = await axiosInstance.get('/user/2fa/status');
      if (res.data?.success) {
        setTwoFactorStatus(res.data.data);
        if (res.data.data.phone_number) {
          setSmsPhone(res.data.data.phone_number);
        }
      }
    } catch (err) {
      console.error('Failed to load 2FA status:', err);
    } finally {
      setIsLoading2Fa(false);
    }
  };

  // Password strength check
  const checkPasswordStrength = (password) => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password)
    });
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData({ ...passwordData, [field]: value });
    if (field === 'new_password') {
      checkPasswordStrength(value);
    }
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validatePassword = () => {
    const errors = {};
    if (!passwordData.current_password) {
      errors.current_password = 'Current password is required';
    }
    if (!passwordData.new_password) {
      errors.new_password = 'New password is required';
    } else if (passwordData.new_password.length < 8) {
      errors.new_password = 'Password must be at least 8 characters';
    } else if (!passwordStrength.uppercase || !passwordStrength.lowercase || !passwordStrength.number) {
      errors.new_password = 'Password must contain uppercase, lowercase and number';
    }
    if (passwordData.new_password !== passwordData.new_password_confirmation) {
      errors.new_password_confirmation = 'Passwords do not match';
    }
    return errors;
  };

  const handleChangePassword = async () => {
    const errors = validatePassword();
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setIsSaving(true);
    setPasswordErrors({});

    try {
      const response = await axiosInstance.post('/user/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
        new_password_confirmation: passwordData.new_password_confirmation
      });

      if (response.data.success) {
        toast.success('Password changed successfully!');
        setSuccessMessage('Password changed successfully!');
        setPasswordData({ 
          current_password: '', 
          new_password: '', 
          new_password_confirmation: '' 
        });
        setPasswordStrength({
          length: false,
          uppercase: false,
          lowercase: false,
          number: false
        });
        setShowPasswordForm(false);
        setPasswordErrors({});
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.response?.data?.errors) {
        setPasswordErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
        setPasswordErrors({ 
          current_password: [error.response.data.message] 
        });
      } else {
        toast.error('Failed to change password');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // =====================================
  // 2FA SETUP: AUTHENTICATOR APP
  // =====================================
  const startAuthenticatorSetup = async () => {
    try {
      setIsLoadingAuthSetup(true);
      const res = await axiosInstance.post('/user/2fa/setup/authenticator');
      if (res.data?.success) {
        setAuthSetupData(res.data.data);
      }
    } catch (err) {
      console.error('Error starting authenticator setup:', err);
      toast.error('Failed to initialize authenticator setup.');
    } finally {
      setIsLoadingAuthSetup(false);
    }
  };

  const handleOpenSetupWizard = (tab = 'authenticator') => {
    setActiveTab(tab);
    setVerificationCode('');
    setSmsDevOtp('');
    setEmailDevOtp('');
    setSmsEmailBackupNotice(false);
    setSmsCarrierSent(false);
    setShow2FASetup(true);
    if (tab === 'authenticator') {
      startAuthenticatorSetup();
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setVerificationCode('');
    setSmsDevOtp('');
    setEmailDevOtp('');
    setSmsEmailBackupNotice(false);
    setSmsCarrierSent(false);
    if (tab === 'authenticator' && !authSetupData.secret) {
      startAuthenticatorSetup();
    }
  };

  const handleCopySecret = () => {
    if (!authSetupData.secret) return;
    navigator.clipboard.writeText(authSetupData.secret);
    setCopiedSecret(true);
    toast.success('Secret copied to clipboard!');
    setTimeout(() => setCopiedSecret(false), 2000);
  };

  const handleVerifyAuthenticator = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      toast.error('Please enter the 6-digit code from your authenticator app.');
      return;
    }

    try {
      setIsVerifying(true);
      const res = await axiosInstance.post('/user/2fa/enable/authenticator', {
        code: verificationCode.trim()
      });

      if (res.data?.success) {
        toast.success('Authenticator App 2FA enabled successfully!');
        setShow2FASetup(false);
        setRecoveryCodes(res.data.recovery_codes || []);
        setShowRecoveryModal(true);
        fetch2FaStatus();
      }
    } catch (err) {
      console.error('Error enabling authenticator 2FA:', err);
      toast.error(err.response?.data?.message || 'Invalid verification code. Please check your authenticator app.');
    } finally {
      setIsVerifying(false);
    }
  };

  // =====================================
  // 2FA SETUP: MOBILE SMS OTP
  // =====================================
  const handleSendSmsOtp = async () => {
    if (!smsPhone || smsPhone.trim().length < 8) {
      toast.error('Please enter a valid mobile number with country code (e.g. +91 9876543210).');
      return;
    }

    try {
      setIsSendingSms(true);
      setSmsSentNotice('');
      setSmsDevOtp('');
      setSmsEmailBackupNotice(false);
      setSmsCarrierSent(false);
      const res = await axiosInstance.post('/user/2fa/setup/sms', {
        phone_number: smsPhone.trim()
      });

      if (res.data?.success) {
        toast.success(res.data.message || 'SMS verification code sent!');
        setSmsSentNotice(res.data.message);
        setSmsCooldown(45);
        setSmsCarrierSent(Boolean(res.data.carrier_sent));
        const code = res.data.dev_otp || res.data.otp;
        if (code) {
          setSmsDevOtp(code);
          setVerificationCode(code);
        }
        if (res.data.email_backup_sent) {
          setSmsEmailBackupNotice(true);
        }
      }
    } catch (err) {
      console.error('Error sending SMS OTP:', err);
      toast.error(err.response?.data?.message || 'Failed to send SMS code. Please try again.');
    } finally {
      setIsSendingSms(false);
    }
  };

  const handleVerifySmsOtp = async () => {
    if (!verificationCode || verificationCode.trim().length !== 6) {
      toast.error('Please enter the 6-digit SMS verification code.');
      return;
    }

    try {
      setIsVerifying(true);
      const res = await axiosInstance.post('/user/2fa/enable/sms', {
        code: verificationCode.trim(),
        phone_number: smsPhone.trim()
      });

      if (res.data?.success) {
        toast.success('Mobile SMS 2FA enabled successfully!');
        setShow2FASetup(false);
        setRecoveryCodes(res.data.recovery_codes || []);
        setShowRecoveryModal(true);
        fetch2FaStatus();
      }
    } catch (err) {
      console.error('Error enabling SMS 2FA:', err);
      toast.error(err.response?.data?.message || 'Invalid SMS code. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  // =====================================
  // 2FA SETUP: EMAIL OTP
  // =====================================
  const handleSendEmailOtp = async () => {
    try {
      setIsSendingEmail(true);
      setEmailSentNotice('');
      setEmailDevOtp('');
      const res = await axiosInstance.post('/user/2fa/setup/email');

      if (res.data?.success) {
        toast.success(res.data.message || 'Verification code sent to your email!');
        setEmailSentNotice(res.data.message);
        setEmailCooldown(45);
        const code = res.data.dev_otp || res.data.otp;
        if (code) {
          setEmailDevOtp(code);
          setVerificationCode(code);
        }
      }
    } catch (err) {
      console.error('Error sending email OTP:', err);
      toast.error(err.response?.data?.message || 'Failed to send email code.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (!verificationCode || verificationCode.trim().length !== 6) {
      toast.error('Please enter the 6-digit email verification code.');
      return;
    }

    try {
      setIsVerifying(true);
      const res = await axiosInstance.post('/user/2fa/enable/email', {
        code: verificationCode.trim()
      });

      if (res.data?.success) {
        toast.success('Email 2FA enabled successfully!');
        setShow2FASetup(false);
        setRecoveryCodes(res.data.recovery_codes || []);
        setShowRecoveryModal(true);
        fetch2FaStatus();
      }
    } catch (err) {
      console.error('Error enabling Email 2FA:', err);
      toast.error(err.response?.data?.message || 'Invalid email verification code. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  // =====================================
  // RECOVERY CODES
  // =====================================
  const handleCopyRecoveryCodes = () => {
    if (!recoveryCodes.length) return;
    const text = recoveryCodes.join('\n');
    navigator.clipboard.writeText(text);
    setCopiedCodes(true);
    toast.success('Recovery codes copied to clipboard!');
    setTimeout(() => setCopiedCodes(false), 2000);
  };

  const handleDownloadRecoveryCodes = () => {
    if (!recoveryCodes.length) return;
    const content = `Kerol Systems AMS - Two-Factor Authentication Recovery Codes\nAccount: ${twoFactorStatus.email}\nGenerated: ${new Date().toLocaleString()}\n\nEach code can be used ONCE if you lose access to your primary 2FA method:\n\n${recoveryCodes.map((c, i) => `${i + 1}. ${c}`).join('\n')}\n\nKeep these codes in a safe place.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kerol-ams-2fa-recovery-codes.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Recovery codes downloaded!');
  };

  const handleRegenerateCodes = async () => {
    if (!window.confirm('Regenerating will invalidate your old recovery codes. Are you sure?')) {
      return;
    }
    try {
      setIsRegeneratingCodes(true);
      const res = await axiosInstance.post('/user/2fa/recovery-codes');
      if (res.data?.success) {
        setRecoveryCodes(res.data.recovery_codes || []);
        setShowRecoveryModal(true);
        toast.success('Recovery codes regenerated!');
        fetch2FaStatus();
      }
    } catch (err) {
      console.error('Error regenerating codes:', err);
      toast.error('Failed to regenerate recovery codes.');
    } finally {
      setIsRegeneratingCodes(false);
    }
  };

  // =====================================
  // DISABLE 2FA
  // =====================================
  const handleDisable2Fa = async (e) => {
    e?.preventDefault();
    setDisableError('');

    if (!disablePassword) {
      setDisableError('Please enter your current password to confirm.');
      return;
    }

    try {
      setIsDisabling2Fa(true);
      const res = await axiosInstance.post('/user/2fa/disable', {
        password: disablePassword
      });

      if (res.data?.success) {
        toast.success('Two-factor authentication disabled.');
        setShowDisableModal(false);
        setDisablePassword('');
        fetch2FaStatus();
      }
    } catch (err) {
      console.error('Error disabling 2FA:', err);
      setDisableError(err.response?.data?.message || 'Incorrect password.');
    } finally {
      setIsDisabling2Fa(false);
    }
  };

  const getMethodBadge = (method) => {
    switch (method) {
      case 'authenticator':
        return { label: 'Authenticator App (TOTP)', icon: 'Smartphone', color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300' };
      case 'sms':
        return { label: 'Mobile SMS OTP', icon: 'Phone', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300' };
      case 'email':
        return { label: 'Email OTP', icon: 'Mail', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300' };
      default:
        return { label: 'Active', icon: 'Shield', color: 'bg-primary/10 text-primary' };
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      
      <main className={`pt-16 transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'}`}>
        <div className="p-6">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground">Security Settings</h1>
            <p className="text-muted-foreground mt-2">
              Manage your account security, passwords, and multi-factor authentication
            </p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg animate-in fade-in slide-in-from-top-2">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={18} className="text-success" />
                <span className="text-sm text-success-foreground">{successMessage}</span>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* ========================================================= */}
            {/* 1. PASSWORD SECTION                                      */}
            {/* ========================================================= */}
            <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-card-foreground">Change Password</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Update your password to keep your account secure
                  </p>
                </div>
                {!showPasswordForm && (
                  <Button
                    variant="outline"
                    onClick={() => setShowPasswordForm(true)}
                    iconName="Key"
                    iconPosition="left"
                  >
                    Change Password
                  </Button>
                )}
              </div>

              {showPasswordForm && (
                <div className="space-y-4 max-w-xl pt-2">
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">
                      Current Password
                    </label>
                    <Input
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                      error={passwordErrors.current_password}
                      placeholder="Enter current password"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">
                      New Password
                    </label>
                    <Input
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                      error={passwordErrors.new_password}
                      placeholder="Enter new password (min. 8 characters)"
                    />
                    {passwordData.new_password && (
                      <div className="mt-2.5 p-3 bg-muted/30 rounded-lg border border-border/50 space-y-1.5">
                        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Password Requirements</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs">
                          <div className="flex items-center space-x-1.5">
                            <Icon 
                              name={passwordStrength.length ? 'CheckCircle' : 'XCircle'} 
                              size={13} 
                              className={passwordStrength.length ? 'text-success' : 'text-muted-foreground'} 
                            />
                            <span className={passwordStrength.length ? 'text-success font-medium' : 'text-muted-foreground'}>
                              Min 8 characters
                            </span>
                          </div>
                          <div className="flex items-center space-x-1.5">
                            <Icon 
                              name={passwordStrength.uppercase ? 'CheckCircle' : 'XCircle'} 
                              size={13} 
                              className={passwordStrength.uppercase ? 'text-success' : 'text-muted-foreground'} 
                            />
                            <span className={passwordStrength.uppercase ? 'text-success font-medium' : 'text-muted-foreground'}>
                              1 uppercase letter (A-Z)
                            </span>
                          </div>
                          <div className="flex items-center space-x-1.5">
                            <Icon 
                              name={passwordStrength.lowercase ? 'CheckCircle' : 'XCircle'} 
                              size={13} 
                              className={passwordStrength.lowercase ? 'text-success' : 'text-muted-foreground'} 
                            />
                            <span className={passwordStrength.lowercase ? 'text-success font-medium' : 'text-muted-foreground'}>
                              1 lowercase letter (a-z)
                            </span>
                          </div>
                          <div className="flex items-center space-x-1.5">
                            <Icon 
                              name={passwordStrength.number ? 'CheckCircle' : 'XCircle'} 
                              size={13} 
                              className={passwordStrength.number ? 'text-success' : 'text-muted-foreground'} 
                            />
                            <span className={passwordStrength.number ? 'text-success font-medium' : 'text-muted-foreground'}>
                              1 number (0-9)
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-1.5">
                      Confirm New Password
                    </label>
                    <Input
                      type="password"
                      value={passwordData.new_password_confirmation}
                      onChange={(e) => handlePasswordChange('new_password_confirmation', e.target.value)}
                      error={passwordErrors.new_password_confirmation}
                      placeholder="Confirm new password"
                    />
                  </div>

                  <div className="flex items-center space-x-3 pt-2">
                    <Button
                      variant="default"
                      onClick={handleChangePassword}
                      isLoading={isSaving}
                    >
                      Update Password
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordData({ 
                          current_password: '', 
                          new_password: '', 
                          new_password_confirmation: '' 
                        });
                        setPasswordErrors({});
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* ========================================================= */}
            {/* 2. TWO-FACTOR AUTHENTICATION (2FA) SECTION               */}
            {/* ========================================================= */}
            <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
              <div className="flex items-start justify-between flex-wrap gap-4 pb-4 border-b border-border/60">
                <div className="flex items-start space-x-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                    twoFactorStatus.enabled 
                      ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400' 
                      : 'bg-blue-100 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400'
                  }`}>
                    <Icon name="Shield" size={24} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <h3 className="text-lg font-bold text-card-foreground">Two-Factor Authentication (2FA)</h3>
                      {twoFactorStatus.enabled ? (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/80 dark:text-emerald-300 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" />
                          Enabled
                        </span>
                      ) : (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                          Disabled
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                      Protect your account with an extra verification step using an <strong>Authenticator App</strong>, <strong>Mobile SMS OTP</strong>, or <strong>Email OTP</strong>.
                    </p>
                  </div>
                </div>

                {/* Main Action Buttons */}
                <div className="flex items-center gap-2">
                  {twoFactorStatus.enabled ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenSetupWizard(twoFactorStatus.method || 'authenticator')}
                        iconName="Settings"
                        iconPosition="left"
                      >
                        Change Method
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          setDisablePassword('');
                          setDisableError('');
                          setShowDisableModal(true);
                        }}
                      >
                        Disable 2FA
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleOpenSetupWizard('authenticator')}
                      iconName="ShieldCheck"
                      iconPosition="left"
                    >
                      Enable 2FA
                    </Button>
                  )}
                </div>
              </div>

              {/* Status Details Card */}
              {twoFactorStatus.enabled ? (
                <div className="mt-5 p-4 rounded-xl bg-muted/30 border border-border flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground font-medium">Active Verification Channel:</span>
                      <span className={`px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1.5 ${getMethodBadge(twoFactorStatus.method).color}`}>
                        <Icon name={getMethodBadge(twoFactorStatus.method).icon} size={13} />
                        <span>{getMethodBadge(twoFactorStatus.method).label}</span>
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-4 flex-wrap">
                      {twoFactorStatus.method === 'email' && (
                        <span>Linked Email: <strong>{twoFactorStatus.masked_email || twoFactorStatus.email}</strong></span>
                      )}
                      {twoFactorStatus.method === 'sms' && (
                        <span>Linked Mobile: <strong>{twoFactorStatus.masked_phone || twoFactorStatus.phone_number}</strong></span>
                      )}
                      {twoFactorStatus.confirmed_at && (
                        <span>Activated: {new Date(twoFactorStatus.confirmed_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRegenerateCodes}
                      iconName="KeyRound"
                      iconPosition="left"
                      isLoading={isRegeneratingCodes}
                    >
                      Backup Codes
                    </Button>
                  </div>
                </div>
              ) : (
                /* Feature Preview Cards when disabled */
                <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3.5">
                  <div 
                    onClick={() => handleOpenSetupWizard('authenticator')}
                    className="p-4 rounded-xl border border-border/80 bg-card hover:bg-muted/40 transition-all cursor-pointer hover:border-primary/50 group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-2.5 group-hover:scale-105 transition-transform">
                      <Icon name="Smartphone" size={18} />
                    </div>
                    <h4 className="text-sm font-bold text-card-foreground">Authenticator App</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Scan QR code with Google Authenticator, Microsoft Authenticator, Authy, or Apple Passwords.
                    </p>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary mt-3 group-hover:underline">
                      Setup App <Icon name="ArrowRight" size={12} />
                    </span>
                  </div>

                  <div 
                    onClick={() => handleOpenSetupWizard('sms')}
                    className="p-4 rounded-xl border border-border/80 bg-card hover:bg-muted/40 transition-all cursor-pointer hover:border-primary/50 group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-indigo-100 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center mb-2.5 group-hover:scale-105 transition-transform">
                      <Icon name="Phone" size={18} />
                    </div>
                    <h4 className="text-sm font-bold text-card-foreground">Mobile Phone SMS OTP</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Receive a 6-digit one-time SMS verification code on your mobile phone on every sign-in.
                    </p>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary mt-3 group-hover:underline">
                      Setup Mobile OTP <Icon name="ArrowRight" size={12} />
                    </span>
                  </div>

                  <div 
                    onClick={() => handleOpenSetupWizard('email')}
                    className="p-4 rounded-xl border border-border/80 bg-card hover:bg-muted/40 transition-all cursor-pointer hover:border-primary/50 group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-2.5 group-hover:scale-105 transition-transform">
                      <Icon name="Mail" size={18} />
                    </div>
                    <h4 className="text-sm font-bold text-card-foreground">Email OTP</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Receive an instant 6-digit verification code to your registered company email inbox.
                    </p>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary mt-3 group-hover:underline">
                      Setup Email OTP <Icon name="ArrowRight" size={12} />
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* ========================================================= */}
      {/* MODAL 1: 2FA SETUP WIZARD (Authenticator / SMS / Email)    */}
      {/* ========================================================= */}
      {show2FASetup && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setShow2FASetup(false)}
        >
          <div 
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-border w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 sm:p-5 border-b border-border flex items-center justify-between bg-muted/30">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
                  <Icon name="Shield" size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-foreground">Configure Two-Factor Authentication</h3>
                  <p className="text-xs text-muted-foreground">Select your preferred verification method</p>
                </div>
              </div>
              <button
                onClick={() => setShow2FASetup(false)}
                className="w-8 h-8 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground flex items-center justify-center transition-colors"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            {/* Method Tabs */}
            <div className="px-5 pt-3 border-b border-border bg-card">
              <div className="grid grid-cols-3 gap-1.5 p-1 bg-muted/40 rounded-xl text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => handleTabChange('authenticator')}
                  className={`py-2 px-2.5 rounded-lg flex items-center justify-center gap-1.5 transition-all ${
                    activeTab === 'authenticator'
                      ? 'bg-white dark:bg-gray-800 text-primary shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Smartphone" size={14} />
                  <span>App (QR)</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleTabChange('sms')}
                  className={`py-2 px-2.5 rounded-lg flex items-center justify-center gap-1.5 transition-all ${
                    activeTab === 'sms'
                      ? 'bg-white dark:bg-gray-800 text-primary shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Phone" size={14} />
                  <span>Mobile OTP</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleTabChange('email')}
                  className={`py-2 px-2.5 rounded-lg flex items-center justify-center gap-1.5 transition-all ${
                    activeTab === 'email'
                      ? 'bg-white dark:bg-gray-800 text-primary shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Mail" size={14} />
                  <span>Email OTP</span>
                </button>
              </div>
            </div>

            {/* Tab Body */}
            <div className="p-5 sm:p-6 overflow-y-auto space-y-4">
              {/* TAB 1: AUTHENTICATOR APP */}
              {activeTab === 'authenticator' && (
                <div className="space-y-4">
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p className="font-semibold text-foreground">Step 1: Scan QR Code with Authenticator App</p>
                    <p>Open Google Authenticator, Microsoft Authenticator, or Authy on your phone and scan the QR code below.</p>
                  </div>

                  {/* QR Code Container */}
                  <div className="flex flex-col items-center justify-center p-4 bg-slate-50 dark:bg-gray-800/60 rounded-xl border border-border">
                    {isLoadingAuthSetup ? (
                      <div className="w-48 h-48 flex flex-col items-center justify-center gap-2">
                        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
                        <span className="text-xs text-muted-foreground">Generating QR Code...</span>
                      </div>
                    ) : authSetupData.otpauth_url ? (
                      <div className="p-3 bg-white rounded-xl shadow-sm border border-slate-200">
                        <QRCodeSVG value={authSetupData.otpauth_url} size={176} level="M" />
                      </div>
                    ) : (
                      <div className="w-48 h-48 flex items-center justify-center text-xs text-muted-foreground">
                        Failed to generate QR code.
                      </div>
                    )}

                    {/* Manual Secret Key */}
                    {authSetupData.secret && (
                      <div className="mt-3 text-center w-full max-w-xs">
                        <span className="text-[10px] text-muted-foreground block mb-1">Cannot scan? Enter code manually:</span>
                        <div className="flex items-center justify-center gap-1.5 bg-white dark:bg-gray-900 px-3 py-1.5 rounded-lg border border-border text-xs font-mono font-bold tracking-widest text-foreground">
                          <span>{authSetupData.secret}</span>
                          <button
                            type="button"
                            onClick={handleCopySecret}
                            className="text-muted-foreground hover:text-primary p-1 ml-1"
                            title="Copy Secret"
                          >
                            <Icon name={copiedSecret ? "Check" : "Copy"} size={13} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 pt-1">
                    <label className="block text-xs font-semibold text-foreground">
                      Step 2: Enter 6-digit code from your app
                    </label>
                    <Input
                      type="text"
                      maxLength={6}
                      autoFocus
                      placeholder="000000"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                      className="text-center font-mono font-bold text-lg tracking-[5px]"
                    />
                  </div>

                  <div className="pt-2">
                    <Button
                      variant="default"
                      onClick={handleVerifyAuthenticator}
                      isLoading={isVerifying}
                      disabled={verificationCode.length !== 6}
                      className="w-full"
                    >
                      Verify & Activate Authenticator
                    </Button>
                  </div>
                </div>
              )}

              {/* TAB 2: MOBILE SMS OTP */}
              {activeTab === 'sms' && (
                <div className="space-y-4">
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p className="font-semibold text-foreground">Step 1: Enter your mobile phone number</p>
                    <p>We'll dispatch a 6-digit SMS verification code to verify your phone.</p>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-foreground block">Mobile Phone Number</label>
                    <div className="flex gap-2">
                      <Input
                        type="tel"
                        placeholder="+91 9876543210"
                        value={smsPhone}
                        onChange={(e) => setSmsPhone(e.target.value)}
                        className="flex-1 text-sm font-medium"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleSendSmsOtp}
                        isLoading={isSendingSms}
                        disabled={smsCooldown > 0 || !smsPhone.trim()}
                        className="shrink-0 text-xs"
                      >
                        {smsCooldown > 0 ? `Resend (${smsCooldown}s)` : 'Send Code'}
                      </Button>
                    </div>
                  </div>

                  {smsSentNotice && smsCarrierSent && (
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-lg text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
                      <Icon name="CheckCircle" size={14} className="text-emerald-600 shrink-0" />
                      <span>{smsSentNotice}</span>
                    </div>
                  )}

                  {smsSentNotice && !smsCarrierSent && (
                    <div className="p-3.5 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-xl text-xs text-blue-900 dark:text-blue-200 space-y-1.5">
                      <div className="flex items-center gap-2 font-semibold text-blue-800 dark:text-blue-300">
                        <Icon name="Info" size={15} className="text-blue-600 shrink-0" />
                        <span>Carrier SMS not dispatched (No SMS Gateway Key in backend/.env)</span>
                      </div>
                      <p className="text-[11px] text-blue-700 dark:text-blue-300/90 leading-relaxed">
                        To receive physical SMS messages on your phone SIM card, add <code>FAST2SMS_API_KEY</code> in <code>backend/.env</code>.
                        In the meantime, your code is <strong>auto-filled below</strong> and sent to your Gmail inbox!
                      </p>
                    </div>
                  )}

                  {smsDevOtp && (
                    <div className="p-3.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-700/80 rounded-xl space-y-2.5 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs font-semibold text-amber-900 dark:text-amber-200">
                          <Icon name="Key" size={15} className="text-amber-600 shrink-0" />
                          <span>Generated Mobile OTP Code:</span>
                        </div>
                        <span className="px-2.5 py-1 font-mono text-base font-bold tracking-[4px] bg-amber-200/80 dark:bg-amber-900 text-amber-900 dark:text-amber-100 rounded-lg border border-amber-300/80 dark:border-amber-700">
                          {smsDevOtp}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-amber-800 dark:text-amber-300 pt-1.5 border-t border-amber-200/80 dark:border-amber-800">
                        <span className="text-[11px]">
                          {smsEmailBackupNotice
                            ? 'A backup copy of this OTP was also delivered to your registered email.'
                            : 'Carrier SMS dispatched / Testing helper.'}
                        </span>
                        <button
                          type="button"
                          onClick={() => setVerificationCode(smsDevOtp)}
                          className="font-bold underline hover:text-amber-950 dark:hover:text-amber-100 ml-2 cursor-pointer text-xs shrink-0"
                        >
                          Auto-Fill Code
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 pt-2 border-t border-border/60">
                    <label className="block text-xs font-semibold text-foreground">
                      Step 2: Enter 6-digit SMS code
                    </label>
                    <Input
                      type="text"
                      maxLength={6}
                      placeholder="000000"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                      className="text-center font-mono font-bold text-lg tracking-[5px]"
                    />
                  </div>

                  <div className="pt-2">
                    <Button
                      variant="default"
                      onClick={handleVerifySmsOtp}
                      isLoading={isVerifying}
                      disabled={verificationCode.length !== 6}
                      className="w-full"
                    >
                      Verify & Activate Mobile SMS 2FA
                    </Button>
                  </div>
                </div>
              )}

              {/* TAB 3: EMAIL OTP */}
              {activeTab === 'email' && (
                <div className="space-y-4">
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p className="font-semibold text-foreground">Step 1: Send verification code to your email</p>
                    <p>We will send a 6-digit one-time code to <strong>{twoFactorStatus.email || 'your registered email'}</strong>.</p>
                  </div>

                  <div className="p-4 bg-muted/40 rounded-xl border border-border flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Icon name="Mail" size={18} className="text-primary shrink-0" />
                      <span className="text-sm font-semibold text-foreground truncate">
                        {twoFactorStatus.email}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSendEmailOtp}
                      isLoading={isSendingEmail}
                      disabled={emailCooldown > 0}
                      className="shrink-0 text-xs"
                    >
                      {emailCooldown > 0 ? `Resend (${emailCooldown}s)` : 'Send Code'}
                    </Button>
                  </div>

                  {emailSentNotice && (
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-lg text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
                      <Icon name="CheckCircle" size={14} className="text-emerald-600 shrink-0" />
                      <span>{emailSentNotice}</span>
                    </div>
                  )}

                  {emailDevOtp && (
                    <div className="p-3.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-700/80 rounded-xl space-y-2.5 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs font-semibold text-amber-900 dark:text-amber-200">
                          <Icon name="Key" size={15} className="text-amber-600 shrink-0" />
                          <span>Generated Email OTP Code:</span>
                        </div>
                        <span className="px-2.5 py-1 font-mono text-base font-bold tracking-[4px] bg-amber-200/80 dark:bg-amber-900 text-amber-900 dark:text-amber-100 rounded-lg border border-amber-300/80 dark:border-amber-700">
                          {emailDevOtp}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-amber-800 dark:text-amber-300 pt-1.5 border-t border-amber-200/80 dark:border-amber-800">
                        <span className="text-[11px]">Check your Gmail inbox or auto-fill below:</span>
                        <button
                          type="button"
                          onClick={() => setVerificationCode(emailDevOtp)}
                          className="font-bold underline hover:text-amber-950 dark:hover:text-amber-100 ml-2 cursor-pointer text-xs shrink-0"
                        >
                          Auto-Fill Code
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 pt-2 border-t border-border/60">
                    <label className="block text-xs font-semibold text-foreground">
                      Step 2: Enter 6-digit email verification code
                    </label>
                    <Input
                      type="text"
                      maxLength={6}
                      placeholder="000000"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                      className="text-center font-mono font-bold text-lg tracking-[5px]"
                    />
                  </div>

                  <div className="pt-2">
                    <Button
                      variant="default"
                      onClick={handleVerifyEmailOtp}
                      isLoading={isVerifying}
                      disabled={verificationCode.length !== 6}
                      className="w-full"
                    >
                      Verify & Activate Email 2FA
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 2: RECOVERY CODES DISPLAY                           */}
      {/* ========================================================= */}
      {showRecoveryModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setShowRecoveryModal(false)}
        >
          <div 
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-border w-full max-w-lg p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400 flex items-center justify-center font-bold">
                  <Icon name="KeyRound" size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-foreground">Backup Recovery Codes</h3>
                  <p className="text-xs text-muted-foreground">Save these codes in a safe place</p>
                </div>
              </div>
              <button
                onClick={() => setShowRecoveryModal(false)}
                className="w-8 h-8 rounded-lg hover:bg-muted text-muted-foreground flex items-center justify-center"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
              <strong>Important:</strong> If you lose access to your phone or email, each code can be used <strong>once</strong> to sign in to your account.
            </div>

            {/* Codes Grid */}
            <div className="grid grid-cols-2 gap-2.5 p-4 bg-slate-50 dark:bg-gray-800/60 rounded-xl border border-border">
              {recoveryCodes.map((code, idx) => (
                <div 
                  key={idx}
                  className="bg-white dark:bg-gray-900 p-2 rounded-lg border border-slate-200 dark:border-gray-700 text-center font-mono font-bold text-sm tracking-wider text-foreground select-all"
                >
                  {code}
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between gap-2 pt-2">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyRecoveryCodes}
                  iconName={copiedCodes ? "Check" : "Copy"}
                  iconPosition="left"
                >
                  {copiedCodes ? "Copied!" : "Copy All"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadRecoveryCodes}
                  iconName="Download"
                  iconPosition="left"
                >
                  Download .txt
                </Button>
              </div>
              <Button
                variant="default"
                size="sm"
                onClick={() => setShowRecoveryModal(false)}
              >
                I Have Saved Them
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 3: DISABLE 2FA CONFIRMATION                         */}
      {/* ========================================================= */}
      {showDisableModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setShowDisableModal(false)}
        >
          <div 
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-border w-full max-w-md p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400 flex items-center justify-center font-bold">
                  <Icon name="AlertCircle" size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-foreground">Disable Two-Factor Authentication</h3>
                  <p className="text-xs text-muted-foreground">Confirm with your account password</p>
                </div>
              </div>
              <button
                onClick={() => setShowDisableModal(false)}
                className="w-8 h-8 rounded-lg hover:bg-muted text-muted-foreground flex items-center justify-center"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <p className="text-xs text-muted-foreground leading-relaxed">
              Disabling Two-Factor Authentication removes the extra security layer from your account. Please enter your current password to confirm:
            </p>

            <form onSubmit={handleDisable2Fa} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  Current Password
                </label>
                <Input
                  type="password"
                  autoFocus
                  placeholder="Enter your current password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  error={disableError}
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDisableModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="destructive"
                  size="sm"
                  isLoading={isDisabling2Fa}
                  disabled={!disablePassword}
                >
                  Confirm & Disable 2FA
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <footer className={`bg-card border-t border-border py-4 transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'}`}>
        <div className="px-6">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center space-x-4">
              <span>© {new Date()?.getFullYear()} AssetTracker Pro</span>
              <span>•</span>
              <span>Security Settings</span>
            </div>
            <div className="flex items-center space-x-4">
              <span>Last updated: {new Date().toLocaleDateString()}</span>
              <span>•</span>
              <span>Version 1.0.0</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SecuritySettings;
